import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Heart, Users, Sparkles, Award, Globe } from 'lucide-react';

const AboutUsPage = () => {
  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">About Life Decoder</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Empowering individuals to unlock their destiny through the ancient wisdom of Chinese Metaphysics
          </p>
        </div>

        {/* Our Story */}
        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Life Decoder was founded with a singular vision: to make the profound wisdom of Chinese Metaphysics 
                accessible to everyone, regardless of their background or experience level.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                For centuries, knowledge of BaZi, Feng Shui, and Qi Men Dun Jia was reserved for emperors, 
                scholars, and the elite. We believe this ancient wisdom belongs to everyone who seeks to 
                understand themselves better and navigate life's challenges with clarity.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Today, Life Decoder has helped thousands of students from over 30 countries discover their 
                potential, make better decisions, and create lives aligned with their true nature.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-gray-800 bg-gradient-to-br from-amber-500/10 to-purple-500/10">
                <div className="w-full h-full flex items-center justify-center">
                  <Sparkles className="w-32 h-32 text-amber-400/50" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-gray-300 leading-relaxed">
                To demystify Chinese Metaphysics and provide practical, actionable guidance that helps 
                individuals understand their strengths, navigate challenges, and make empowered decisions 
                in career, relationships, and life.
              </p>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-gray-300 leading-relaxed">
                To become the world's leading platform for Chinese Metaphysics education, creating a 
                global community of empowered individuals who use ancient wisdom to thrive in the modern world.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Excellence</h3>
              <p className="text-gray-400">
                We are committed to delivering the highest quality education and maintaining the integrity 
                of traditional teachings while making them relevant for today.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Compassion</h3>
              <p className="text-gray-400">
                We approach every student with empathy and understanding, creating a supportive environment 
                where everyone can learn and grow at their own pace.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Community</h3>
              <p className="text-gray-400">
                We believe in the power of connection and foster a vibrant community where students support 
                each other on their journey of self-discovery.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-gray-800 rounded-2xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">10,000+</p>
                <p className="text-gray-400">Students Worldwide</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">30+</p>
                <p className="text-gray-400">Countries</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">15+</p>
                <p className="text-gray-400">Years Experience</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">98%</p>
                <p className="text-gray-400">Satisfaction Rate</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-gray-400 mb-8">
            Discover the ancient wisdom that can transform your life
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link 
              to="/courses" 
              className="px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors"
            >
              Explore Courses
            </Link>
            <Link 
              to="/consultation-fees" 
              className="px-8 py-3 border border-gray-700 text-white hover:bg-gray-800 font-medium rounded-lg transition-colors"
            >
              Book Consultation
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUsPage;
